import React, { useEffect, useState } from "react";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Button, Checkbox } from "flowbite-react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../../../Layout";
import NetworkIndicator from "../../../../Practice/Components/NetworkIndicator.jsx";
import ImportQuestionCard from "../../../Components/ImportQuestionCard";
import { gql, useMutation } from "@apollo/client";
import {
  useGetQuestionsByCategoryQuery,
  useAllCategoriesQuery,
  useGetCourseQuery,
  useGetAllCoursesQuery,
} from "../../../../../graphql/generated.tsx";
import { toast } from "../../../../../toast";
import { FaSearch } from "react-icons/fa";
import { getAuth } from "firebase/auth";

// 🔹 Mutation to request deletion/approval
const REQUEST_DELETION = gql`
  mutation RequestDeletion(
    $typeOfDeletion: String!
    $payload: JSON!
    $reasonForDeletion: String!
    $requestedBy: String!
    $title: String!
  ) {
    requestDeletion(
      typeOfDeletion: $typeOfDeletion
      payload: $payload
      reasonForDeletion: $reasonForDeletion
      requestedBy: $requestedBy
      title: $title
    ) {
      id
      typeOfDeletion
      payload
      statusOfApproval
      requestedBy
      requestedAt
    }
  }
`;

// 🔹 Mapping of course IDs to course names
const courseNameMap = {
  "bd08c319-e092-4615-b966-6a2c59e32541": "Core Java",
  "3963a263-b1c7-4c6b-8278-913c0b6916e9": "Service Company Preparation",
  "b610cb03-3858-4a09-91f3-1af3c1739ca1": "Product Company Preparation",
  "2acc9b91-6b47-4bf7-8afc-214290cb0649": "Expertise in Programming",
  "c1caf724-e4c9-4bd2-b05b-4696b63145f1": "Code for Beginners: From Zero to Hero",
  "4bc08c0e-fc0d-46ac-927b-3fd9a5f0d5f8": "DSA Foundation",
  "091823e4-1f53-46ce-bae8-b7d18c8490b6": "Programming Foundation",
  "93489be8-9f53-402b-af19-926f497dba40": "Algorithms",
};

const ImportQuestion = () => {
  const { cat_id, course_id } = useParams();
  const [catName, setCatName] = useState("");
  const [questions, setQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(cat_id);
  const questionsPerPage = 10;
  const navigate = useNavigate();

  const { data: courseData } = useGetCourseQuery({
    variables: { id: course_id },
    skip: !course_id,
  });

  const { data: allCoursesData } = useGetAllCoursesQuery();

  // 🔹 Fetch all categories for dropdown
  const { data: CategoriesData } = useAllCategoriesQuery();
  const allCategories = CategoriesData?.allCategories || [];

  // 🔹 Fetch questions by selected category
  const {
    data: FilterByCategoryQuestions,
    refetch,
  } = useGetQuestionsByCategoryQuery({
    variables: {
      categoryId: selectedCategory,
      courseId: course_id,
      limit: questionsPerPage,
      page: currentPage,
      searchName: searchQuery,
    },
    onError: (err) => toast.error(`Error fetching questions: ${err.message}`),
  });

  useEffect(() => {
    if (FilterByCategoryQuestions?.getQuestionsByCategory?.questions) {
      setCatName(
        FilterByCategoryQuestions?.getQuestionsByCategory?.category?.name || ""
      );
      setQuestions(FilterByCategoryQuestions?.getQuestionsByCategory.questions);
    }
  }, [FilterByCategoryQuestions]);

  useEffect(() => {
    refetch();
  }, [selectedCategory, searchQuery, currentPage, refetch]);

  const totalPages = FilterByCategoryQuestions?.getQuestionsByCategory
    ?.totalCount
    ? Math.ceil(
        FilterByCategoryQuestions?.getQuestionsByCategory.totalCount /
          questionsPerPage
      )
    : 0;

  const handleCheckboxChange = (e, questionId) => {
    if (e.target.checked) {
      setSelectedQuestions([...selectedQuestions, questionId]);
    } else {
      setSelectedQuestions(selectedQuestions.filter((id) => id !== questionId));
    }
  };

  const [requestDeletion, { loading }] = useMutation(REQUEST_DELETION);

  const getCourseNameById = (id, fallback) => {
    const allCourses = allCoursesData?.getAllCourses || [];
    const courseFromList = allCourses.find((course) => course.id === id)?.name;
    if (courseFromList) return courseFromList;
    if (id === course_id && courseData?.getCourse?.name) {
      return courseData.getCourse.name;
    }
    return courseNameMap[id] || fallback;
  };

  const handleSave = async () => {
    if (selectedQuestions.length === 0) {
      toast.error("Please select at least one question.");
      return;
    }

    const auth = getAuth();
    const user = auth.currentUser;
    const token = user ? await user.getIdToken(true) : "";
    const userData = JSON.parse(localStorage.getItem("user"));
    const userDisplayName =
      userData?.displayName || user?.displayName || userData?.email || "Admin";

    const targetCourseName = getCourseNameById(course_id, "Unknown Course");

    const selectedQuesData = questions.filter((q) =>
      selectedQuestions.includes(q.id)
    );

    const reasonForDeletion = selectedQuesData
      .map((q) => {
        const sourceCourseName = getCourseNameById(q.course_id, "Unknown Source");
        const sourceCategoryName = allCategories.find(c => c.id === selectedCategory)?.name || "Unknown Category";
        return `Request by ${userDisplayName} to add "${q.title}" to "${targetCourseName}" from "${sourceCourseName}" (Category: ${sourceCategoryName})`;
      })
      .join("; ");

    const payload = {
      operationName: "addQuestionsToCourse",
      variables: {
        courseId: course_id,
        questionIds: selectedQuestions,
      },
      query: `
        mutation addQuestionsToCourse($courseId: String!, $questionIds: [String!]!) {
          addQuestionsToCourse(courseId: $courseId, questionIds: $questionIds) {
            id
            name
            description
            question_ids
          }
        }
      `,
      token,
    };

    try {
      await requestDeletion({
        variables: {
          typeOfDeletion: "IMPORT_QUESTIONS",
          payload,
          reasonForDeletion,
          requestedBy: userDisplayName,
          title: `Import ${selectedQuestions.length} Question(s) to ${targetCourseName}`,
        },
      });

      toast.success("Request to add questions sent for approval!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Failed to send request for adding questions");
    }
  };

  return (
    <AdminLayout>
      <NetworkIndicator />
      <div className="p-8">
        {/* 🔹 Header Section */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-500 hover:text-gray-700 focus:outline-none w-[100px]"
          >
            <span className="flex items-center gap-2 text-sm font-medium">
              <IoMdArrowRoundBack className="w-6 h-6" />
              Go Back
            </span>
          </button>
          <h2 className="text-3xl font-bold text-center max-md:text-2xl">
            {catName} Questions
          </h2>
          <Button
            onClick={handleSave}
            disabled={selectedQuestions.length === 0 || loading}
            className="text-white bg-primary"
          >
            {loading ? "Sending Request..." : "Save"}
          </Button>
        </div>

        {/* 🔹 Search + Import Section */}
        <div className="flex flex-col items-center mb-4">
          <div className="flex items-center justify-center mb-3">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="border border-gray-300 rounded-md px-3 py-2 w-[300px] mr-2"
            />
            <Button onClick={() => refetch()} className="px-4 text-white bg-primary">
              Search
            </Button>
          </div>

          {/* 🔹 Import Button */}
          <Button
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="px-4 text-white bg-primary"
          >
            {showCategoryDropdown ? "Hide Categories" : "Import from Another Category"}
          </Button>
        </div>

        {/* 🔹 Category Dropdown (Below Search Bar) */}
        {showCategoryDropdown && (
          <div className="flex justify-center mb-6">
            <div className="w-[300px]">
              <label
                htmlFor="categories"
                className="block mb-2 text-center text-gray-700"
              >
                Select Category to Import From
              </label>
              <select
                id="categories"
                value={selectedCategory}
                onChange={(e) => {
                  setSelectedCategory(e.target.value);
                  setCurrentPage(1);
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                {allCategories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* 🔹 Questions List */}
        <div className="mt-5">
          {questions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-[500px] text-center">
              <FaSearch className="mb-4 text-gray-400 text-7xl" />
              <h2 className="mb-2 text-2xl font-bold text-gray-600">
                Oops, No Results Found
              </h2>
              <p className="text-gray-500 mb-6 lg:w-[500px]">
                It looks like we couldn't find any question to import.
              </p>
            </div>
          ) : (
            questions.map((question) => (
              <div
                className="flex items-center justify-center w-full px-8 py-4 mb-2 bg-white rounded-md shadow-md"
                key={question._id}
              >
                <Checkbox
                  className="pb-3"
                  onChange={(e) => handleCheckboxChange(e, question.id)}
                  checked={selectedQuestions.includes(question.id)}
                />
                <div className="w-full">
                  <ImportQuestionCard data={question} />
                </div>
              </div>
            ))
          )}

          {/* 🔹 Pagination */}
          <div className="flex justify-between mt-4">
            <Button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-0 text-white bg-primary"
            >
              Previous
            </Button>
            <span className="text-gray-700">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-0 text-white bg-primary"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ImportQuestion;
